import React, { useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Stack,
  Divider,
  Button,
  TextField,
} from "@mui/material";
import Tag from "../components/Tag";
import { auth } from "../../services/firebase/auth";
import {
  approveOrganizerRequest,
  rejectOrganizerRequest,
} from "../services/organizerRequests";

function Row({ label, value }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "120px 1fr",
        gap: 1,
        py: 0.7,
      }}
    >
      <Typography
        sx={{ color: "text.secondary", fontSize: 12, fontWeight: 800 }}
      >
        {label}
      </Typography>
      <Typography sx={{ fontWeight: 800 }}>{value || "—"}</Typography>
    </Box>
  );
}

export default function OrganizerRequestDrawer({ open, onClose, item }) {
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);

  const statusTone = useMemo(() => {
    if (!item?.status) return "default";
    return item.status;
  }, [item]);

  const reviewerUid = auth.currentUser?.uid || null;

  const approve = async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      await approveOrganizerRequest(item.id, { reviewerUid, notes });
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  const reject = async () => {
    if (!item?.id) return;
    setLoading(true);
    try {
      await rejectOrganizerRequest(item.id, { reviewerUid, notes });
      onClose?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 520 },
          p: 2.4,
          bgcolor: "background.default",
        },
      }}
    >
      <Stack spacing={1.6}>
        <Box>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontWeight: 1000, fontSize: 18 }}>
              Solicitação
            </Typography>
            <Tag label={item?.status || "—"} tone={statusTone} />
          </Stack>
          <Typography sx={{ color: "text.secondary", fontSize: 12, mt: 0.4 }}>
            Protocolo: {item?.id || "—"}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ p: 2, borderRadius: 1, bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 1000, mb: 0.6 }}>
            Organizador
          </Typography>
          <Row label="Nome" value={item?.organizer?.name} />
          <Row label="E-mail" value={item?.organizer?.email} />
          <Row label="WhatsApp" value={item?.organizer?.phone} />
          <Row label="Cidade/UF" value={item?.organizer?.cityState} />
          <Row label="Tipo" value={item?.organizer?.type} />
        </Box>

        <Box sx={{ p: 2, borderRadius: 1, bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 1000, mb: 0.6 }}>Evento</Typography>
          <Row label="Nome" value={item?.event?.name} />
          <Row label="Tipo" value={item?.event?.type} />
          <Row label="Cidade/UF" value={item?.event?.cityState} />
          <Row label="Data" value={item?.event?.date} />
          <Row label="Freq." value={item?.event?.frequency} />
        </Box>

        <Box sx={{ p: 2, borderRadius: 1, bgcolor: "background.paper" }}>
          <Typography sx={{ fontWeight: 1000, mb: 0.6 }}>Financeiro</Typography>
          <Row label="Vende hoje" value={item?.finance?.alreadySells} />
          <Row label="Plataforma" value={item?.finance?.currentPlatform} />
          <Row label="Público" value={item?.finance?.audienceRange} />
          <Row label="Preço" value={item?.finance?.priceRange} />
          <Row label="Antecip." value={item?.finance?.wantsAdvance} />
        </Box>

        <TextField
          label="Notas do reviewer (opcional)"
          multiline
          minRows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ bgcolor: "background.paper", borderRadius: 1 }}
        />

        <Divider />

        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.2}>
          <Button
            onClick={approve}
            disabled={loading || item?.status !== "pending"}
            variant="contained"
            sx={{ flex: 1, fontWeight: 1000 }}
          >
            Aprovar
          </Button>
          <Button
            onClick={reject}
            disabled={loading || item?.status !== "pending"}
            variant="outlined"
            color="error"
            sx={{ flex: 1, fontWeight: 1000 }}
          >
            Reprovar
          </Button>
          <Button onClick={onClose} variant="text" sx={{ flex: 1 }}>
            Fechar
          </Button>
        </Stack>

        {item?.status !== "pending" && item?.review ? (
          <Typography sx={{ color: "text.secondary", fontSize: 12 }}>
            Última review: {item?.review?.by || "—"} •{" "}
            {String(item?.review?.notes || "").slice(0, 120)}
          </Typography>
        ) : null}
      </Stack>
    </Drawer>
  );
}
